import React from "react";
import { Collapse, Form, Row, Col, Select, Input, Space, Button, Spin } from "antd";
import { RootStateOrAny, useSelector } from "react-redux";
import MaskedInput from "antd-mask-input";
import debounce from "lodash/debounce";
import LocalStorageService from "../../services/LocalStorageService";
import { FormFieldValue } from "../../lib/interfaces/FormFieldValue";
import usePhoneMask from "../../lib/hooks/usePhoneMask";
import { checkIsLoading } from "../../lib/utils";
import { useAppDispatch, useAppSelector } from "../..";
import { cleanAccountSelect, fetchAccountsByName } from "../../store/slices/accounts";

const { Panel } = Collapse;
const { Option } = Select;

interface IProps {
  handlePageableSearch: (values: FormFieldValue[]) => void;
  advancedSearchFormValues: FormFieldValue[];
  setAdvancedSearchFormValues: React.Dispatch<React.SetStateAction<FormFieldValue[] | undefined>>;
  setIsPageableSearch: React.Dispatch<React.SetStateAction<boolean>>;
  isPageableSearch: boolean;
  setSearchByNameFields: React.Dispatch<React.SetStateAction<FormFieldValue[] | undefined>>;
  searchByNameForm: any;
  advancedSearchForm: any;
}

function SearchFilters(props: IProps) {
  const {
    handlePageableSearch,
    setIsPageableSearch,
    advancedSearchFormValues,
    setAdvancedSearchFormValues,
    isPageableSearch,
    setSearchByNameFields,
    advancedSearchForm,
    searchByNameForm,
  } = props;
  const dispatch = useAppDispatch();
  const { accountStatuses, accountTypes } = useSelector((state: RootStateOrAny) => state.dicts);
  const { accountSelect, accountSelectLoading } = useAppSelector((state) => state.accounts);
  const isCollapseOpened = isPageableSearch ? 1 : 0;
  const { phoneMask } = usePhoneMask();

  const searchAccounts = (value) => {
    if (value) {
      // @ts-ignore
      dispatch(fetchAccountsByName({ searchParam: value }));
    }
  };

  const debouncedSearchAccounts = debounce(searchAccounts, 800);

  const onClearOrganizationFullName = () => {
    // @ts-ignore
    dispatch(cleanAccountSelect());
  };

  const onFinish = (values) => {
    const parsedValues: FormFieldValue[] = [];

    Object.keys(values).forEach((key) => {
      if (values[key] !== "" && values[key] !== undefined) {
        parsedValues.push({ name: key, value: values[key] });
      }
    });
    handlePageableSearch(parsedValues);
    setAdvancedSearchFormValues(parsedValues);
    setIsPageableSearch(true);
    setSearchByNameFields(undefined);
    searchByNameForm.resetFields();
  };

  const onReset = () => {
    advancedSearchForm.resetFields();
    setAdvancedSearchFormValues(undefined);
    setIsPageableSearch(false);
    handlePageableSearch([]);
  };

  const accountSelectContent = (accountSelect as any)?.content || [];

  return (
    <Collapse expandIconPosition="right" defaultActiveKey={isCollapseOpened}>
      <Panel header="Расширенный поиск" key="1">
        <Form
          style={{ marginTop: 20 }}
          form={advancedSearchForm}
          wrapperCol={{ span: 19 }}
          fields={advancedSearchFormValues}
          onFinish={onFinish}
          labelAlign="left"
        >
          <Row gutter={50} justify="center">
            <Col span={11}>
              <Form.Item name="fullName" label="Полное наименование" labelCol={{ span: 6 }}>
                <Select
                  showSearch
                  allowClear
                  notFoundContent={checkIsLoading(accountSelectLoading) && <Spin size="small" />}
                  onSearch={debouncedSearchAccounts}
                  onClear={onClearOrganizationFullName}
                >
                  {accountSelectContent.map((item) => (
                    <Select.Option
                      key={item.id}
                      value={item?.organization?.fullName}
                      title={item?.organization?.fullName}
                    >
                      {item?.organization?.fullName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="phone"
                label="Контактный телефон"
                initialValue=""
                labelCol={{ span: 6 }}
              >
                <MaskedInput
                  mask={phoneMask.full}
                  name="phone"
                  aria-autocomplete="none"
                  allowClear
                />
              </Form.Item>
              <Form.Item
                name="propertyStringValue"
                labelCol={{ span: 6 }}
                label={LocalStorageService.getTenantId() === "1" ? "БИН" : "ИНН"}
              >
                <Input aria-autocomplete="none" allowClear />
              </Form.Item>

              <Form.Item name="externalId" label="Технический ID" labelCol={{ span: 6 }}>
                <Input aria-autocomplete="none" allowClear />
              </Form.Item>
              <Form.Item
                name="id"
                label="ID аккаунтов"
                labelCol={{ span: 6 }}
                rules={[
                  {
                    validator: (x, value) => {
                      if (value && /[^0-9,]/.test(value)) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    },
                    message: "В качестве разделителя может использоваться только запятая",
                  },
                ]}
              >
                <Input
                  allowClear
                  aria-autocomplete="none"
                  placeholder='Введите ID аккаунтов через ","'
                />
              </Form.Item>
            </Col>
            <Col span={11}>
              <Form.Item name="agreementNumber" label="Номер договора" labelCol={{ span: 5 }}>
                <Input aria-autocomplete="none" allowClear />
              </Form.Item>
              <Form.Item name="accountStatusId" label="Статус аккаунта" labelCol={{ span: 5 }}>
                <Select allowClear>
                  {accountStatuses.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="accTypeId" label="Тип аккаунта" labelCol={{ span: 5 }}>
                <Select allowClear>
                  {accountTypes.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="billDeliveryEmail" label="Email" labelCol={{ span: 5 }}>
                <Input aria-autocomplete="none" allowClear />
              </Form.Item>
              <Form.Item
                name="agreementId"
                label="ID договоров"
                labelCol={{ span: 5 }}
                rules={[
                  {
                    validator: (x, value) => {
                      if (value && /[^0-9,]/.test(value)) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    },
                    message: "В качестве разделителя может использоваться только запятая",
                  },
                ]}
              >
                <Input
                  aria-autocomplete="none"
                  allowClear
                  placeholder='Введите ID договоров через ","'
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end">
            <Space>
              <Button htmlType="reset" onClick={onReset}>
                Сброс параметров
              </Button>

              <Button type="primary" htmlType="submit">
                Применить
              </Button>
            </Space>
          </Row>
        </Form>
      </Panel>
    </Collapse>
  );
}

export default SearchFilters;
